import React from "react";

export default function Hero() {
  return (
    <div className="overflow-hidden">
      {/* Elegant serif for the name — swap the font stack below if your project already loads one */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500&family=Jost:wght@300;400;500&display=swap');
        .hero-display { font-family: 'Cormorant Garamond', Georgia, serif; }
        .hero-body { font-family: 'Jost', sans-serif; }
      `}</style>

      <div
        className="relative w-full h-screen"
        style={{
          backgroundImage:
            "linear-gradient(135deg, rgba(18, 16, 13, 0.88) 0%, rgba(32, 26, 19, 0.85) 50%, rgba(14, 12, 10, 0.9) 100%), url('/assets/adam.webp')",
          backgroundAttachment: "fixed",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        {/* Subtle Background Pattern */}
        <div className="absolute inset-0 opacity-[0.04]">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `radial-gradient(circle at 25% 25%, #c9a876 1px, transparent 1px),
                             radial-gradient(circle at 75% 75%, #c9a876 1px, transparent 1px)`,
              backgroundSize: "60px 60px",
            }}
          ></div>
        </div>

        {/* Mystical Floating Elements — softened, no blinking */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 left-10 w-1 h-1 bg-[#c9a876] rounded-full"></div>
          <div className="absolute top-40 right-20 w-0.5 h-0.5 bg-[#e8dcc4] rounded-full"></div>
          <div className="absolute bottom-40 left-1/4 w-1 h-1 bg-[#c9a876] rounded-full"></div>
          <div className="absolute top-60 right-1/3 w-0.5 h-0.5 bg-[#e8dcc4] rounded-full"></div>
        </div>

        {/* Main Content Container */}
        <div className="relative flex flex-col md:flex-row items-center justify-center md:justify-between h-full px-4 sm:px-8 md:px-12 lg:px-20 py-8">
          {/* Left Section - Text Content */}
          <div className="flex-1 max-w-2xl text-center md:text-left order-2 md:order-1 mt-8 md:mt-0">
            <div className="space-y-4 md:space-y-6">
              {/* Decorative Line */}
              <div className="flex items-center justify-center md:justify-start space-x-4 mb-6 md:mb-8">
                <div className="w-8 md:w-12 h-px bg-[#c9a876]"></div>
                <div className="w-2 h-2 border border-[#c9a876] rotate-45"></div>
              </div>

              {/* Main Name */}
              <h1 className="hero-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-9xl font-light text-[#f5f0e6] leading-none tracking-wide">
                Joseph
              </h1>
              <h1 className="hero-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-9xl font-light text-[#f5f0e6] leading-none tracking-wide -mt-2 md:-mt-4">
                Munnangi
              </h1>

              {/* Portfolio Label */}
              <div className="flex items-center justify-center md:justify-start space-x-4 md:space-x-6 mt-6 md:mt-8">
                <div className="w-12 md:w-16 h-px bg-[#e8dcc4] opacity-40"></div>
                <h2 className="hero-body text-lg sm:text-xl md:text-2xl lg:text-3xl font-light text-[#c9a876] tracking-[0.3em] uppercase">
                  Portfolio
                </h2>
              </div>

              {/* Subtitle */}
              <p className="text-base sm:text-lg md:text-xl text-gray-400 mt-4 md:mt-6 max-w-md mx-auto md:mx-0 leading-relaxed">
                Adventure Curator - Munnangi Joseph Sukumar
              </p>

              {/* Decorative Bottom Element */}
              <div className="flex items-center justify-center md:justify-start space-x-3 mt-8 md:mt-12">
                <div className="w-6 md:w-8 h-px bg-[#c9a876]"></div>
                <div className="w-1 h-1 bg-[#c9a876] rounded-full"></div>
                <div className="w-3 md:w-4 h-px bg-[#c9a876]"></div>
                <div className="w-1 h-1 bg-[#c9a876] rounded-full"></div>
              </div>
            </div>
          </div>

          {/* Right Section - Image */}
          <div className="flex-shrink-0 order-1 md:order-2 md:ml-8 lg:ml-12">
            <div className="relative">
              {/* Main Image Container */}
              <div className="w-64 h-80 sm:w-72 sm:h-90 md:w-80 md:h-96 lg:w-96 lg:h-[450px] xl:w-[420px] xl:h-[500px] relative overflow-hidden">
                <img
                  src="/assets/abcd.webp"
                  alt="Joseph Munnangi"
                  className="w-full h-full object-cover filter brightness-[1.03] contrast-[1.03] saturate-[0.92]"
                />

                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>

                {/* Border Frame */}
                <div className="absolute inset-0 border border-[#c9a876]/40"></div>
              </div>

              {/* Decorative Corner Elements */}
              <div className="absolute -top-2 md:-top-3 -left-2 md:-left-3 w-4 md:w-6 h-4 md:h-6 border-l-2 border-t-2 border-[#c9a876]"></div>
              <div className="absolute -top-2 md:-top-3 -right-2 md:-right-3 w-4 md:w-6 h-4 md:h-6 border-r-2 border-t-2 border-[#c9a876]"></div>
              <div className="absolute -bottom-2 md:-bottom-3 -left-2 md:-left-3 w-4 md:w-6 h-4 md:h-6 border-l-2 border-b-2 border-[#c9a876]"></div>
              <div className="absolute -bottom-2 md:-bottom-3 -right-2 md:-right-3 w-4 md:w-6 h-4 md:h-6 border-r-2 border-b-2 border-[#c9a876]"></div>

              {/* Side Accent Lines - Hidden on mobile */}
              <div className="hidden md:block absolute -left-8 top-1/4 w-4 h-px bg-[#e8dcc4] opacity-30"></div>
              <div className="hidden md:block absolute -left-8 top-1/2 w-6 h-px bg-[#c9a876] opacity-60"></div>
              <div className="hidden md:block absolute -left-8 top-3/4 w-4 h-px bg-[#e8dcc4] opacity-30"></div>
            </div>
          </div>
        </div>

        {/* Bottom Fade */}
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-black/30 to-transparent"></div>
      </div>
    </div>
  );
}
